import React, { useEffect, useState } from "react";
import { IDog } from "@eagerdog/interfaces";
import { TITLE_TRACKERS } from "@eagerdog/constants";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import OptionButtons from "src/components/OptionButtons/OptionButtons";
import Button from "src/components/Button/Button";

import styles from "./RunResultQuickAction.module.scss";

export interface ISportOption {
    value: string;
    label: string;
    sanctioningClub: string;
}

interface IProps {
    onClose: () => void;
    /** Called once the user has picked a dog + sport and wants to move to the actual run-entry form. */
    onContinue: (dog: IDog, sport: ISportOption) => void;
    /** When set (e.g. from a dog's profile), the dog is fixed and only the sport is chosen. */
    dog?: IDog;
}

// Sourced from TITLE_TRACKERS — the same show_type/sanctioning_club pairing the
// Ribbon Room tab uses to decide which sport cards to render, so this stays
// correct as that list changes instead of maintaining a second copy here.
export const SPORT_OPTIONS: ISportOption[] = Object.values(TITLE_TRACKERS).map((config) => ({
    value: config.show_type,
    label: config.label,
    sanctioningClub: config.sanctioning_club,
}));

const SPORT_DROPDOWN_OPTIONS: IOption[] = SPORT_OPTIONS.map((s) => ({ id: s.value, value: s.label }));

const RunResultQuickAction: React.FC<IProps> = ({ onClose, onContinue, dog: fixedDog }) => {
    const user = helperService.getUser();

    const [dogs, setDogs] = useState<IDog[]>(fixedDog ? [fixedDog] : []);
    const [dogId, setDogId] = useState<string>(fixedDog?._id || "");
    const [sportType, setSportType] = useState<string>(SPORT_OPTIONS[0].value);
    const [loading, setLoading] = useState<boolean>(!fixedDog);

    useEffect(() => {
        if (!user._id || fixedDog) return;
        apiService.getUserDogs(user._id).then((data) => {
            setDogs(data);
            if (data.length > 0) setDogId(data[0]._id);
        }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user._id]);

    const dogOptions: IOption[] = dogs.map((dog) => ({ id: dog._id, value: dog.call_name }));
    const selectedDog = dogs.find((d) => d._id === dogId);
    const selectedSport = SPORT_OPTIONS.find((s) => s.value === sportType) || SPORT_OPTIONS[0];

    const dogDropdownValue: IOption = dogOptions.find((o) => o.id === dogId) || { id: "", value: loading ? "Loading your dogs…" : "No dogs on your account" };
    const sportDropdownValue: IOption = SPORT_DROPDOWN_OPTIONS.find((o) => o.id === sportType) || SPORT_DROPDOWN_OPTIONS[0];

    return (
        <div className={styles.RunResultQuickAction}>
            <div className={styles.title}>
                {fixedDog ? `Log a run for ${fixedDog.call_name}` : "Log a run result"}
            </div>

            {!fixedDog && (
                <Dropdown
                    label="Which dog?"
                    placeholder="Choose a dog"
                    options={dogOptions}
                    value={dogDropdownValue}
                    disabled={dogOptions.length === 0}
                    onChange={(_e, value) => setDogId(value.id)}
                />
            )}

            <OptionButtons
                className={styles.sportOptions}
                label="Which sport?"
                options={SPORT_DROPDOWN_OPTIONS}
                value={sportDropdownValue}
                onChange={(_e, value) => setSportType(value.id)}
            />

            <div className={styles.footer}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    disabled={!selectedDog}
                    onClick={() => selectedDog && onContinue(selectedDog, selectedSport)}
                >
                    Continue
                </Button>
            </div>
        </div>
    );
};

export default RunResultQuickAction;
